import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import queryClient from "../configs/react-query/query-client";
import queryKeys from "../configs/react-query/query-key";
import { PROGRESS_FOSSID_STATUS, VERIFICATION_TYPE } from "../constants/index";
import { processComponentConflictFlags } from "../utils/common";
import { useFetchData } from "./useFetchData";
import { useMutationData } from "./useMutationData";
import { FORCE_COMPLETE_VERIFICATION_ACTION } from "@constants/action";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { getExternalApiErrorMessage } from "@utils/error";
import { useTranslation } from "react-i18next";

export const useFossidVerification = (
  projectSeq,
  sourceCodeVerificationSeq,
  modifiedComponentData = [],
  sourceCodeScanFollowupResults = [],
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [verificationStatus, setVerificationStatus] = useState("ON_ANALYSIS");
  const [approvalFlag, setApprovalFlag] = useState("N");
  const [projectData, setProjectData] = useState({});
  const [verificationData, setVerificationData] = useState({});
  const [currentActionData, setCurrentActionData] = useState({});
  const [verificationReportsData, setVerificationReportsData] = useState([]);
  const [verificationComment, setVerificationComment] = useState("");
  const [verificationFile, setVerificationFile] = useState(null);
  const [approvalComment, setApprovalComment] = useState("");
  const [isMovingToFulfillment, setIsMovingToFulfillment] = useState(false);
  // Create a ref to access FulfillmentInfo component methods
  const fulfillmentInfoRef = useRef(null);

  // Fetch project data
  const { data: fetchedProjectData, isLoading: projectDataLoading, isFetching: projectDataFetching } = useFetchData(
    [queryKeys.getProjectByProjectId, projectSeq],
    `/project/${projectSeq}`,
    { method: "GET" },
    {},
    { enabled: !!projectSeq },
  );

  // Fetch verification data
  const { data: fetchedVerificationData, error: fetchedVerificationDataError, isLoading: verificationDataLoading, isFetching: verificationDataFetching } = useFetchData(
    [queryKeys.getFossidById, sourceCodeVerificationSeq],
    `/${projectSeq}/source_code_verification/${sourceCodeVerificationSeq}`,
    { method: "GET" },
    {},
    { enabled: !!sourceCodeVerificationSeq },
  );

  useEffect(() => {
    if (fetchedVerificationDataError) {
      if (fetchedVerificationDataError?.response?.status === 404) {
        navigate("/404", { state: { backTo: "/projects" } });
      }
    }
  }, [fetchedVerificationDataError]);

  // Mutation for updating approval flag
  const updateApprovalFlagMutation = useMutationData(
    "/source_code_verification_approval_flag",
    { method: "PUT" },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setApprovalFlag("Y");
      },
      onError: (error) => {
        console.error("Error updating approval flag:", error);
      },
    },
  );

  const updateVerificationStatusToReviewMutation = useMutationData(
    "/source_code_verification_status?preStatus=",
    { method: "PUT" },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REVIEW);
        // Invalidate relevant queries to ensure fresh data
        queryClient.invalidateQueries({
          queryKey: [
            queryKeys.getSourceCodeScanComponentByProjectIdAndVerificationVersion,
            projectId,
            verificationVersion,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getFossidById, sourceCodeVerificationSeq],
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getProjectByProjectId, projectSeq],
        });

        // Navigate to project detail after successful API call
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error updating verification status:", error);
      },
    },
  );

  // Unified mutation for rejecting verification with flexible preStatus and targetStatus
  const rejectVerificationMutation = useMutationData(
    (data) => {
      const { preStatus } = data;
      return `/source_code_verification_status${preStatus ? `?preStatus=${preStatus}` : ""}`;
    },
    {
      method: "PUT",
    },
    {
      onSuccess: (response, variables) => {
        // Update local state after successful API call
        const { verificationStatusCd } = variables;
        setVerificationStatus(verificationStatusCd);
        // Reset the approval request flag
        setApprovalFlag("N");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error rejecting verification:", error);
      },
    },
  );

  const updateSourceCodeVerificationToFulfillmentMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_FOSSID_STATUS.REVIEW },
    },
    {
      onSuccess: () => {
        if (verificationFile) {
          const formData = new FormData();
          formData.append("file", verificationFile);
          formData.append("sourceCodeVerificationSeq", sourceCodeVerificationSeq);
          uploadSourceCodeVerificationFileMutation.mutate(formData);
        }
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
    },
  );

  // New mutation for decompleting verification to ON_VERIFICATION instead of ON_REVIEW
  const decompleteVerificationMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_FOSSID_STATUS.VERIFICATION,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.VERIFICATION);
        // Reset the approval flags
        setApprovalFlag("N");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error decompleting verification:", error);
      },
    },
  );

  // New mutation for decompleting verification to ON_VERIFICATION instead of ON_REVIEW
  const decompleteFromDoneMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_FOSSID_STATUS.DONE,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error decompleting verification:", error);
      },
    },
  );

  const completeVerification = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        if (verificationFile) {
          const formData = new FormData();
          formData.append("file", verificationFile);
          formData.append("sourceCodeVerificationSeq", sourceCodeVerificationSeq);
          uploadSourceCodeVerificationFileMutation.mutate(formData);
        }
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.DONE);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error complete verification request:", error);
      },
    },
  );

  const moveToDoneMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
        action: FORCE_COMPLETE_VERIFICATION_ACTION,
      },
    },
    {
      onSuccess: () => {
        if (verificationFile) {
          const formData = new FormData();
          formData.append("file", verificationFile);
          formData.append("sourceCodeVerificationSeq", sourceCodeVerificationSeq);
          uploadSourceCodeVerificationFileMutation.mutate(formData);
        }
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.DONE);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error move to done verification request:", error);
      },
    },
  );


  const updateApprovalRequestFlagMutation = useMutationData(
    "/source_code_verification_approval_request_flag",
    {
      method: "PUT",
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error updating approval request flag:", error);
        const errorMessage = getExternalApiErrorMessage(error, t);
        useSuccessToast(TOAST.error, t("common.error"), errorMessage);
      },
    },
  );

  const fileGroupNo = fetchedProjectData?.fileGroupNo;
  const licensePolicySeq = fetchedProjectData?.licensePolicySeq;
  const verificationVersion = fetchedVerificationData?.verificationVersion;
  const projectId = fetchedVerificationData?.projectId;

  // Fetch file info
  const { data: fetchedFileInfo } = useFetchData(
    [queryKeys.getFileInfoByFileGroupNo, fileGroupNo],
    `/file_info_by_file_group_no/${fileGroupNo}`,
    { method: "GET" },
    {},
    { enabled: !!fileGroupNo },
  );

  // Fetch license policy
  const { data: fetchedLicensePolicy, isLoading: licensePolicyLoading, isFetching: licensePolicyFetching } = useFetchData(
    [queryKeys.getLicensePolicyByLicensePolicySeq, licensePolicySeq],
    `/license_policy/${licensePolicySeq}`,
    { method: "GET" },
    {},
    { enabled: !!licensePolicySeq },
  );

  const { data: fetchedSourceCodeScanComponent, isLoading: sourceCodeScanComponentLoading, isFetching: sourceCodeScanComponentFetching } = useFetchData(
    [queryKeys.getSourceCodeScanComponentByProjectIdAndVerificationVersion, projectId, verificationVersion],
    "/source_code_scan_component",
    {
      method: "GET",
      params: {
        projectId: projectId,
        verificationVersion: verificationVersion,
      },
    },
    {},
    { enabled: !!projectId && !!verificationVersion },
  );

  const {
    data: fetchedSourceCodeScanFollowupPlan,
    dataUpdatedAt: sourceCodeScanFollowupPlanUpdatedAt,
  } = useFetchData(
    ["source_code_scan_followup_plan", projectId, verificationVersion],
    "/source_code_scan_followup_plan",
    {
      method: "GET",
      params: {
        projectId: projectId,
        verificationVersion: verificationVersion,
      },
    },
    {},
    {
      enabled: !!projectId && !!verificationVersion,
      refetchOnMount: true,
      staleTime: 0,
    },
  );

  const fetchedVerificationReportForPopup = useMutationData(
    "/verification_report_for_popup",
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (response && Array.isArray(response)) {
          setVerificationReportsData(response);
        }
      },
      onError: (error) => {
        console.error("Error getting verification report for popup:", error);
      },
    },
  );

  const { data: fetchedSourceCodeScanDependency } = useFetchData(
    [queryKeys.getSourceCodeScanDependencyByProjectIdAndVerificationVersion, projectId, verificationVersion],
    "/source_code_scan_dependency",
    {
      method: "GET",
      params: {
        projectId: projectId,
        verificationVersion: verificationVersion,
      },
    },
    {},
    { enabled: !!projectId && !!verificationVersion },
  );

  const { data: fetchedSourceCodeAction } = useFetchData(
    [queryKeys.getSourceCodeActionBySourceCodeVerificationSeq, sourceCodeVerificationSeq],
    "/source_code_action",
    {
      method: "GET",
      params: {
        verificationSeq: sourceCodeVerificationSeq,
      },
    },
    {},
    { enabled: !!sourceCodeVerificationSeq },
  );

  const moveToFulfillmentMutation = useMutationData(
    "/source_code_verification_followup_results",
    {
      method: "PUT",
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error move to fulfillment:", error);
      },
    },
  );

  const moveToRequestMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REQUEST);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error move to request:", error);
      },
    },
  );

  const moveToVerificationMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.VERIFICATION);
        setApprovalFlag("Y");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error move to request:", error);
      },
    },
  );

  const moveToUsageTypeReviewMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error move to request:", error);
      },
    },
  );

  const updateSourceCodeActionMutation = useMutationData(
    "/source_code_action",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (response) {
          setCurrentActionData(response);
        }
      },
      onError: (error) => {
        console.error("Error updating source code action:", error);
      },
    },
  );

  const updateSourceCodeActionForActionMutation = useMutationData(
    "/source_code_action_for_action",
    { method: "PUT" },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error updating source code action:", error);
      },
    },
  );

  // Mutation for uploading fulfillment file
  const uploadFulfillmentFileMutation = useMutationData(
    "/source_code_verification_upload_for_followup",
    { method: "POST" },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error uploading fulfillment file:", error);
      },
    },
  );

  const deCompleteFulfillmentMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.FULFILLMENT);
        navigate("/fulfillment-process");
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const rejectReqFulfillmentMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_FOSSID_STATUS.FULFILLMENT,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REVIEW);
        navigate(`/projects/${projectSeq}/fossid/${sourceCodeVerificationSeq}`);
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const completeCheckFulfillmentMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT,
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REVIEW);
        navigate(`/projects/${projectSeq}/fossid/${sourceCodeVerificationSeq}`);
      },
      onError: (error) => {
        console.error("Error force to done verification request:", error);
      },
    },
  );

  const updateVerificationStatusToReVerificationMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: {
        preStatus: "",
      },
    },
    {
      onSuccess: () => {
        // Update local state after successful API call
        setVerificationStatus(PROGRESS_FOSSID_STATUS.REVERIFICATION);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error update verification status to re verification:", error);
      },
    },
  );

  const reUploadVerificationFileMutation = useMutationData(
    queryKeys.sourceCodeVerificationUpload,
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("common.re-upload-file-success"));
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        let errorMessage;
        // Only show error message becase customer's api upload
        if (error?.response?.data && (error?.response?.data?.code === "20300") && error?.response?.data?.additionalMessage) {
          const { additionalMessage } = error.response.data;
          errorMessage = additionalMessage;
        } else {
          // Fallback to extractErrorCode if no response data
          const errorCode = extractErrorCode(error, "common.error-occurred");
          errorMessage = t(errorCode);
        }

        useSuccessToast(TOAST.error, t("common.error"), errorMessage);
      },
    },
  );

  const addVerificationReportMutation = useMutationData(
    "/verification_report",
    { method: "POST" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("common.verification-report-added-success"));
      },
      onError: (error) => {
        console.error("Error adding verification report:", error);
      },
    },
  );

  const updateSourceCodeVerificationMutation = useMutationData(
    "/source_code_verification",
    {
      method: "PUT",
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error update verification status to re verification:", error);
      },
    },
  );

  const uploadSourceCodeVerificationFileMutation = useMutationData(
    "/source_code_verification_upload_for_verification",
    {
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error during upload data:", error);
      },
    },
  );

  const moveToLoadIdentificationsMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: { preStatus: "" },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error move to load identifications:", error);
      },
    },
  );

  const updateApprovalFlagForUsageTypeMutation = useMutationData(
    "/source_code_verification_approval_flag",
    {
      method: "PUT",
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error updating approval flag for usage type:", error);
      },
    },
  );

  const rejectVerificationFromReviewMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: { preStatus: "REJECT_ON_REVIEW" },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error reject verification from review:", error);
      },
    },
  );

  const rejectVerificationFromOnVerificationMutation = useMutationData(
    "/source_code_verification_status",
    {
      method: "PUT",
      params: { preStatus: PROGRESS_FOSSID_STATUS.VERIFICATION },
    },
    {
      onSuccess: () => {
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      },
      onError: (error) => {
        console.error("Error reject verification from on verification:", error);
      },
    },
  );

  const updateSourceCodeScanComponentsMutation = useMutationData(
    "/source_code_scan_components",
    {
      method: "PUT",
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error updating source code scan components:", error);
      },
    },
  );

  const handleGetVerificationReportForPopup = () => {
    fetchedVerificationReportForPopup.mutate({
      projectId: projectId,
      verificationToolCd: VERIFICATION_TYPE.FOSSID,
      verificationVersion: verificationVersion,
      sortColumnName: "created_at",
      sortOrder: "desc",
    });
  };

  // Update project data when fetched
  useEffect(() => {
    if (fetchedProjectData) {
      setProjectData(fetchedProjectData);
    }
  }, [fetchedProjectData]);

  // Update verification data when fetched
  useEffect(() => {
    if (fetchedVerificationData) {
      setVerificationData(fetchedVerificationData);
      setVerificationStatus(fetchedVerificationData.verificationStatusCd);
      setApprovalFlag(fetchedVerificationData.approvalFlag);
      setVerificationComment(fetchedVerificationData.verificationComment);
      setVerificationFile(fetchedVerificationData.verificationRealFileName);
      // Initialize approval comment from fetched data
      setApprovalComment(fetchedVerificationData.approvalComment || "");
    }
  }, [fetchedVerificationData]);

  const handleUpdateStatus = (newApprovalFlag, newStatus) => {
    if (newApprovalFlag === "Y") {
      // Call the API to update the approval flag
      updateApprovalFlagMutation.mutate({
        approvalFlag: "Y",
        sourceCodeVerificationSeq: sourceCodeVerificationSeq,
        approvalComment: approvalComment,
      });
    }

    // Still update the local state
    setApprovalFlag(newApprovalFlag);
    if (newStatus) {
      setVerificationStatus(newStatus);
    }
  };

  const handleMoveToReview = () => {
    // Determine which component data to use
    let componentDataToSend = modifiedComponentData;

    // If no modifications were made, use the original fetched data and process conflict flags
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedSourceCodeScanComponent || [], fetchedLicensePolicy);
    } else {
      // If modifications were made, process conflict flags on the modified data
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    return updateVerificationStatusToReviewMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REVIEW,
      sourceCodeScanComponents: componentDataToSend,
      sourceCodeScanFollowupResults: [],
      verificationComment: verificationComment,
    });
  };

  // Function to handle rejection of verification requests
  const handleRejectVerification = (currentStep, preStatusCd) => {
    // Determine target status based on current step
    let verificationStatusCd;
    switch (preStatusCd) {
      case PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT:
        verificationStatusCd = PROGRESS_FOSSID_STATUS.FULFILLMENT;
        break;
      case PROGRESS_FOSSID_STATUS.FULFILLMENT:
        verificationStatusCd = PROGRESS_FOSSID_STATUS.REVIEW;
        break;
      case PROGRESS_FOSSID_STATUS.REVIEW:
        verificationStatusCd = PROGRESS_FOSSID_STATUS.VERIFICATION;
        break;
      default:
        verificationStatusCd = PROGRESS_FOSSID_STATUS.ANALYSIS_CHECK;
        break;
    }

    // Create payload without preStatus
    const payload = {
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: verificationStatusCd,
    };

    // Pass preStatus separately to the mutation
    const dataWithPreStatus = {
      ...payload,
      preStatus: preStatusCd,
    };

    return rejectVerificationMutation.mutateAsync(dataWithPreStatus);
  };

  // New function to handle decompleting from DONE to VERIFICATION
  const handleDecompleteVerification = () => {
    decompleteVerificationMutation.mutate({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT,
    });
  };

  const handleCompleteVerification = () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : sourceCodeScanFollowupResults;
    const payload = {
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.DONE,
      verificationComment: verificationComment,
      sourceCodeScanFollowupResults: updatedFollowupResults
    }
    return completeVerification.mutateAsync(payload);
  };

  const handleMoveToDone = () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : sourceCodeScanFollowupResults;
    const payload = {
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.DONE,
      verificationComment: verificationComment,
      sourceCodeScanFollowupResults: updatedFollowupResults
    };
    return moveToDoneMutation.mutateAsync(payload);
  };

  const handleMoveToFulfillment = async () => {
    // Get the latest fulfillment data from the component if ref is available
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : sourceCodeScanFollowupResults;

    // Process component data with conflict flags
    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedSourceCodeScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    try {
      // Wait for the first mutation to complete
      await moveToFulfillmentMutation.mutateAsync({
        sourceCodeVerificationSeq: sourceCodeVerificationSeq,
        sourceCodeScanFollowupResults: updatedFollowupResults,
      });

      // Then execute the second mutation
      await updateSourceCodeVerificationToFulfillmentMutation.mutateAsync({
        sourceCodeVerificationSeq: sourceCodeVerificationSeq,
        verificationStatusCd: PROGRESS_FOSSID_STATUS.FULFILLMENT,
        sourceCodeScanFollowupResults: updatedFollowupResults,
        sourceCodeScanComponents: componentDataToSend,
        verificationComment: verificationComment,
      });
    } catch (error) {
      console.error("Error in handleMoveToFulfillment:", error);
      throw error;
    }
  };

  const handleMoveToRequest = () => {
    return moveToRequestMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REQUEST,
    });
  };

  const handleMoveToVerification = () => {
    // Process component data with conflict flags
    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedSourceCodeScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    moveToVerificationMutation.mutate({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
      sourceCodeScanComponents: componentDataToSend,
    });
  };

  const handleMoveToUsageTypeReview = () => {
    moveToUsageTypeReviewMutation.mutate({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
    });
  };

  const handleDecompleteFromDone = () => {
    return decompleteFromDoneMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT,
    });
  };

  const handleUpdateSourceCodeAction = (actionData) => {
    const { completionRequestDt, actionsContent, actionVerificationSeq } = actionData;
    let formattedDate = completionRequestDt || "";
    if (formattedDate && /^\d{4}-\d{2}-\d{2}$/.test(formattedDate)) {
      formattedDate = `${formattedDate}T00:00:00.000Z`;
    }
    const payload = {
      actionsContent: actionsContent || "",
      completionRequestDt: formattedDate,
      sourceCodeVerificationSeq: actionVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.DONE,
      approvedFlag: "N",
      actedFlag: "N",
    };
    updateSourceCodeActionMutation.mutate(payload);
  };

  const handleUpdateSourceCodeActionForAction = (actionData) => {
    const { actionSeq, actionsResultContent } = actionData;
    const { actionSeq: currentActionSeq } = currentActionData;
    const payload = {
      actionSeq: currentActionSeq || actionSeq,
      actionsResultContent: actionsResultContent || "",
      actedFlag: "Y",
    };
    updateSourceCodeActionForActionMutation.mutate(payload);
  };

  // Handler for forcing fulfillment completion
  const handleForceCompleteFulfillment = () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : sourceCodeScanFollowupResults;
    const payload = {
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.DONE,
    };
    if (fulfillmentInfoRef.current) {
      payload.sourceCodeScanFollowupResults = updatedFollowupResults;
    }
    moveToDoneMutation.mutate(payload);
  };

  // Handler for adding verification report
  const handleAddVerificationReport = (reportData) => {
    addVerificationReportMutation.mutate(reportData);
  };

  const handleToCompleteFulfillment = async () => {
    // Validate that we have the required data
    if (!sourceCodeVerificationSeq) {
      alert("Error: Missing verification sequence. Please refresh the page and try again.");
      return;
    }

    // Get the latest fulfillment data from the component if ref is available
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : sourceCodeScanFollowupResults;

    // Get fulfillment comment from the component
    const fulfillmentComment = fulfillmentInfoRef.current ? fulfillmentInfoRef.current.getFulfillmentComment() : "";

    // Get form data for file upload
    const formData = fulfillmentInfoRef.current ? fulfillmentInfoRef.current.getFormDataForUpload() : new FormData();

    // Add additional required fields to form data
    formData.append("sourceCodeVerificationSeq", sourceCodeVerificationSeq);

    // Process component data with conflict flags
    let componentDataToSend = modifiedComponentData;
    if (!modifiedComponentData || modifiedComponentData.length === 0) {
      componentDataToSend = processComponentConflictFlags(fetchedSourceCodeScanComponent || [], fetchedLicensePolicy);
    } else {
      componentDataToSend = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    }

    try {
      // API Call 1: Update followup results
      await moveToFulfillmentMutation.mutateAsync({
        sourceCodeVerificationSeq: sourceCodeVerificationSeq,
        sourceCodeScanFollowupResults: updatedFollowupResults,
      });

      // API Call 2: Update verification status to CHECK_FULFILLMENT
      await updateSourceCodeVerificationToFulfillmentMutation.mutateAsync({
        sourceCodeVerificationSeq: sourceCodeVerificationSeq,
        verificationStatusCd: PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT,
        sourceCodeScanFollowupResults: updatedFollowupResults,
        sourceCodeScanComponents: componentDataToSend,
        followupComment: fulfillmentComment,
      });

      // API Call 3: Upload fulfillment file
      try {
        await uploadFulfillmentFileMutation.mutateAsync(formData);
        // All APIs completed successfully
        setVerificationStatus(PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT);
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      } catch (uploadError) {
        // Even if file upload fails, the status was already updated
        setVerificationStatus(PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT);
        alert("Fulfillment completed but file upload failed. You may need to upload the file manually.");
        navigate(`/projects/${projectSeq}`, { state: { activeTab: "fossid" } });
      }
    } catch (error) {
      alert("Failed to complete fulfillment. Please check your connection and try again.");
      throw error;
    }
  };

  const handleToDecompleteFulfillment = () => {
    deCompleteFulfillmentMutation.mutate({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.FULFILLMENT,
    });
  };

  const handleToRejectReqFulfillment = () => {
    rejectReqFulfillmentMutation.mutate({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REVIEW,
    });
  };

  const handleToCompleteCheckFulfillment = async () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : sourceCodeScanFollowupResults;

    // Execute mutations sequentially
    await completeCheckFulfillmentMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REVIEW,
      verificationComment: verificationComment,
    });
    await moveToFulfillmentMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      sourceCodeScanFollowupResults: updatedFollowupResults,
    });
  };

  const handleToFollowUpResult = () => {
    const updatedFollowupResults = fulfillmentInfoRef.current
      ? fulfillmentInfoRef.current.getFulfillmentData()
      : sourceCodeScanFollowupResults;

    return moveToFulfillmentMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      sourceCodeScanFollowupResults: updatedFollowupResults,
    });
  };

  const handleToRequestReVerification = (dueDate) => {
    return updateVerificationStatusToReVerificationMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REVERIFICATION,
      reverificationDueDt: dueDate ? dueDate.toISOString() : null,
    });
  };

  const handleReUploadFile = (requestData) => {
    const { requestComment, verifFile } = requestData;
    const formData = new FormData();
    formData.append("sourceCodeVerificationSeq", sourceCodeVerificationSeq);
    formData.append("sourceCodeFile", verifFile[0]);
    reUploadVerificationFileMutation.mutate(formData);
    updateSourceCodeVerificationMutation.mutate({
      sourceCodeVerificationSeq,
      requestComment,
      projectSeq,
      projectId,
      verificationVersion,
    });
  };

  const handleMoveToLoadIdentifications = () => {
    return moveToLoadIdentificationsMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REQUEST,
    });
  };

  const handleMoveToUsageTypeCheck = (approvalRequestFlag = "Y") => {
    return updateApprovalRequestFlagMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
      approvalRequestFlag: approvalRequestFlag,
    });
  };

  const handleMoveToUsageTypeInput = (approvalFlag = "Y") => {
    return updateApprovalFlagForUsageTypeMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
      approvalFlag: approvalFlag,
    });
  };

  const handleRejectVerificationFromReview = () => {
    // Call the mutation to update status
    rejectVerificationFromReviewMutation.mutate({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
    });
  };

  const handleRejectVerificationFromOnVerification = () => {
    rejectVerificationFromOnVerificationMutation.mutate({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.ANALYSIS_CHECK,
    });
  };

  const handleSaveSourceCodeScanComponents = () => {
    // Process component data with conflict flags before saving
    const processedComponentData = processComponentConflictFlags(modifiedComponentData, fetchedLicensePolicy);
    updateSourceCodeScanComponentsMutation.mutate(processedComponentData);
  };

  const handleRejectUsageTypeCheck = () => {
    return updateApprovalRequestFlagMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.REQUEST,
      approvalRequestFlag: "N",
    });
  };

  const handleRejectUseTypeInput = () => {
    return updateApprovalFlagForUsageTypeMutation.mutateAsync({
      sourceCodeVerificationSeq: sourceCodeVerificationSeq,
      verificationStatusCd: PROGRESS_FOSSID_STATUS.VERIFICATION,
      approvalFlag: "N",
    });
  };

  // Computed properties
  const isReviewOrDoneStage =
    verificationStatus === PROGRESS_FOSSID_STATUS.REVIEW || verificationStatus === PROGRESS_FOSSID_STATUS.DONE;

  const isNotAnalysisOrCheckAnalysisOrRequest =
    verificationStatus !== PROGRESS_FOSSID_STATUS.ANALYSIS &&
    verificationStatus !== PROGRESS_FOSSID_STATUS.ANALYSIS_CHECK &&
    verificationStatus !== PROGRESS_FOSSID_STATUS.REQUEST;
  const isDone = verificationStatus === PROGRESS_FOSSID_STATUS.DONE;
  const isNotAnalysis = verificationStatus !== PROGRESS_FOSSID_STATUS.ANALYSIS;
  const isFulfillmentOrCheckFulfillmentStage =
    verificationStatus === PROGRESS_FOSSID_STATUS.FULFILLMENT ||
    verificationStatus === PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT;

  const isCheckFulfillmentStage = verificationStatus === PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT;

  // Combined loading state for all essential API calls
  // Check both isLoading (no data in cache) and isFetching (refetching data)
  const isLoading = 
    projectDataLoading ||
    verificationDataLoading ||
    licensePolicyLoading ||
    sourceCodeScanComponentLoading;

  const isFetching = 
    projectDataFetching ||
    verificationDataFetching ||
    licensePolicyFetching ||
    sourceCodeScanComponentFetching;

  return {
    // Loading state
    isLoading,
    isFetching,

    verificationStatus,
    approvalFlag,
    projectData,
    verificationData,
    projectId,
    verificationVersion,
    fetchedFileInfo,
    fetchedLicensePolicy,
    fetchedSourceCodeScanComponent,
    fetchedSourceCodeScanFollowupPlan,
    sourceCodeScanFollowupPlanUpdatedAt,
    isReviewOrDoneStage,
    isNotAnalysisOrCheckAnalysisOrRequest,
    isDone,
    updateApprovalFlagMutation,
    updateApprovalRequestFlagMutation,
    updateApprovalFlagForUsageTypeMutation,
    fulfillmentInfoRef, // Export the ref
    fetchedSourceCodeScanDependency,
    fetchedSourceCodeAction,
    verificationReportsData,
    isNotAnalysis,
    isFulfillmentOrCheckFulfillmentStage,
    isCheckFulfillmentStage,
    verificationComment,
    verificationFile,
    setVerificationComment,
    setVerificationFile,
    // Export approval comment state
    approvalComment,
    setApprovalComment,
    handleUpdateStatus,
    handleMoveToReview,
    handleRejectVerification,
    handleDecompleteVerification,
    handleMoveToDone,
    handleCompleteVerification,
    handleMoveToFulfillment,
    handleMoveToRequest,
    handleMoveToVerification,
    handleDecompleteFromDone,
    handleUpdateSourceCodeAction,
    handleUpdateSourceCodeActionForAction,
    handleGetVerificationReportForPopup,
    handleForceCompleteFulfillment,
    handleAddVerificationReport,
    handleToCompleteFulfillment,
    handleToDecompleteFulfillment,
    handleToRejectReqFulfillment,
    handleToCompleteCheckFulfillment,
    handleToRequestReVerification,
    handleReUploadFile,
    handleMoveToLoadIdentifications,
    handleMoveToUsageTypeCheck,
    handleMoveToUsageTypeInput,
    handleToFollowUpResult,
    handleRejectVerificationFromReview,
    handleRejectVerificationFromOnVerification,
    handleSaveSourceCodeScanComponents,
    handleRejectUsageTypeCheck,
    handleRejectUseTypeInput,
    // Export loading state
    isMovingToFulfillment,
  };
};

export default useFossidVerification;
